import React, { useState, useEffect, useRef } from "react";
import {
    AppBar, Toolbar, Box, Typography, IconButton,
    Dialog, DialogActions, DialogTitle, Button, useMediaQuery, Tooltip,
    CircularProgress, Chip, Drawer, Stack, Divider, DialogContent, Grid, Paper, TextField, InputAdornment
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import AvatarMenu from "./AvatarMenu";
import ThemeToggle from "./ThemeToggle";

import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { usePlano } from "../../contexts/PlanContext";
import { usePlanModal } from "../../contexts/PlanModalContext";

import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PendingRoundedIcon from '@mui/icons-material/PendingRounded';
import DoNotDisturbOnRoundedIcon from '@mui/icons-material/DoNotDisturbOnRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import AssuredWorkloadRoundedIcon from '@mui/icons-material/AssuredWorkloadRounded';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PixIcon from '@mui/icons-material/Pix';

import ReportProblemIcon from '@mui/icons-material/ReportProblem';

//import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import CelebrationIcon from '@mui/icons-material/Celebration';
import DiscountIcon from '@mui/icons-material/Discount';

import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';

import api from '../../services/api';
import PixSubscriptionDialog from '../Billing/PixSubscriptionDialog';
import { getApiErrorMessage, normalizePlan } from '../../services/apiUtils';

// Ative/controle o período da Promoção aqui
const PROMO_UNTIL = new Date("2025-12-31T23:59:59-03:00");
const IS_PROMO = new Date() <= PROMO_UNTIL;

const STATUS_CONFIG = {
    G: {
        label: "Teste gratuito",
        labelButton: "Planos",
        color: "info",
        tooltip: "Aproveite seu teste gratuito para conhecer todos os recursos.",
        action: true,
        icon: <RocketLaunchRoundedIcon sx={{ color: "info.main", fontSize: 20 }} />
    },
    G0: {
        label: "Teste encerrado",
        labelButton: "Planos",
        color: "warning",
        tooltip: "Seu teste gratuito foi encerrado. Assine para continuar.",
        action: true,
        icon: <WarningAmberRoundedIcon sx={{ color: "warning.main", fontSize: 20 }} />
    },
    A: {
        label: "Plano Ativo",
        color: "success",
        tooltip: "Seu plano está ativo e liberado para uso.",
        action: false,
        icon: <CheckCircleRoundedIcon sx={{ color: "success.main", fontSize: 20 }} />
    },
    B: {
        label: "Parceiro",
        color: "secondary",
        tooltip: "Acesso de parceiro com recursos liberados.",
        action: false,
        icon: <HandshakeIcon sx={{ color: "secondary.main", fontSize: 20 }} />
    },
    P: {
        label: "Assinatura pendente",
        color: "warning",
        tooltip: "O pagamento não foi concluído. Regularize para continuar usando todos os recursos.",
        action: false,
        icon: <PendingRoundedIcon sx={{ color: "warning.main", fontSize: 20 }} />
    },
    C: {
        label: "Plano cancelado",
        labelButton: "Ativar plano",
        color: "error",
        tooltip: "Plano cancelado. Realize nova assinatura para continuar.",
        action: true,
        icon: <DoNotDisturbOnRoundedIcon sx={{ color: "error.main", fontSize: 20 }} />
    }
};

const PromoCountdown = () => {
    const targetDate = new Date("2025-12-31T23:59:59-03:00");

    const getTimeLeft = () => {
        const now = new Date();
        const diff = targetDate.getTime() - now.getTime();

        if (diff <= 0) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0, finished: true };
        }

        const totalSeconds = Math.floor(diff / 1000);
        const days = Math.floor(totalSeconds / (60 * 60 * 24));
        const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return { days, hours, minutes, seconds, finished: false };
    };

    const [timeLeft, setTimeLeft] = useState(getTimeLeft);

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(getTimeLeft());
        }, 1000);

        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!IS_PROMO || timeLeft.finished) return null;

    const pad = (n) => String(n).padStart(2, '0');

    return (
        <Box sx={{ mt: 0, mb: 0, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }} >
                <AccessTimeRoundedIcon sx={{ verticalAlign: "middle", fontSize: 14, mr: 0.3 }} />{timeLeft.days}dias {pad(timeLeft.hours)}h {pad(timeLeft.minutes)}m {pad(timeLeft.seconds)}s
            </Typography>
        </Box>
    );
};

const PLANS = [
    {
        tipo: "PROFESSOR",
        title: "Professor",
        priceDisabled: "R$39,90",
        price: "R$29,90/mês",
        badge: "Essencial",
        desc: "* Para começar a reduzir o trabalho manual.",
        infoText: "* Ideal para professores com baixa demanda de criação.",
        features: [
            "10 documentos com IA por mês",
            "Alinhamento total à BNCC",
            "Edição fácil antes de exportar",
            "Exportação para Word e PDF",
            "Exportação do gabarito",
            "Suporte por WhatsApp"
        ],
        highlight: false
    },
    {
        tipo: "MENTOR",
        title: "Mentor",
        priceDisabled: "R$59,90",
        price: "R$49,90/mês",
        badge: "Mais escolhido",
        desc: "* Para quem produz materiais com frequência.",
        infoText: "* Para professores que produzem conteúdos com frequência.",
        features: [
            "25 documentos com IA por mês",
            "Alinhamento total à BNCC",
            "Edição fácil antes de exportar",
            "Exportação para Word e PDF",
            "Exportação do gabarito",
            "10 imagens com IA por mês",
            "Suporte por WhatsApp"
        ],
        highlight: true
    },
    {
        tipo: "MESTRE",
        title: "Mestre",
        priceDisabled: "R$89,90",
        price: "R$69,90/mês",
        badge: "Mais completo",
        desc: "* Para alta demanda de criação pedagógica.",
        infoText: "* Pensado para professores com alta demanda de criação.",
        features: [
            "50 documentos com IA por mês",
            "Alinhamento total à BNCC",
            "Edição fácil antes de exportar",
            "Exportação para Word e PDF",
            "Exportação do gabarito",
            "20 imagens com IA por mês",
            "Suporte por WhatsApp"
        ],
        highlight: false
    }
];

const PROFESSOR_EXTRAS_RISCADOS = [
    "Geração de imagens com IA"
];

const MENTOR_EXTRAS_RISCADOS = [];

const PLAN_LABEL = {
    PROFESSOR: "Plano Professor",
    MENTOR: "Plano Mentor",
    MESTRE: "Plano Mestre",
};

function parseBRLToNumber(price = "") {
    return Number(
        String(price)
            .replace(/[^\d,]/g, "")
            .replace(".", "")
            .replace(",", ".")
    ) || 0;
}

function formatBRL(value = 0) {
    return Number(value).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
}

function calcDiscountedPrice(valorOriginal, descontoPercentual) {
    const percentual = Math.max(0, Math.min(100, Number(descontoPercentual || 0)));
    const valor = Number(valorOriginal || 0);
    return Math.round((valor * (1 - percentual / 100) + Number.EPSILON) * 100) / 100;
}

function getPlanMonthlyValue(planName) {
    const plan = PLANS.find((item) => item.tipo === planName);
    return parseBRLToNumber(plan?.price);
}

function normalizePixDialogData(raw = {}, planName = null) {
    const pixPayment = raw?.pixPayment && typeof raw.pixPayment === 'object' ? raw.pixPayment : null;
    const source = pixPayment || raw;

    return {
        ...source,
        planName: planName || raw?.planName || raw?.plan || raw?.plano,
        amount: source?.amount || source?.value || source?.valor || getPlanMonthlyValue(planName || raw?.planName || raw?.plan || raw?.plano),
        paymentId: source?.paymentId || source?.payment_id || raw?.paymentId || raw?.payment_id,
        pixPayload: source?.pixPayload || source?.pix_payload || source?.payload || raw?.pixPayload || raw?.pix_payload || raw?.payload,
        pixEncodedImage: source?.pixEncodedImage || source?.pix_encoded_image || source?.encodedImage || raw?.pixEncodedImage || raw?.pix_encoded_image || raw?.encodedImage,
        pixExpirationDate: source?.pixExpirationDate || source?.pix_expiration_date || source?.expirationDate || raw?.pixExpirationDate || raw?.pix_expiration_date || raw?.expirationDate,
        dueDate: source?.dueDate || source?.due_date || raw?.dueDate || raw?.due_date,
        invoiceUrl: source?.invoiceUrl || source?.invoice_url || raw?.invoiceUrl || raw?.invoice_url,
    };
}


export default function Header() {
    const { user } = useAuth();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { planoStatus, restantes, imagensRestantes, loadingPlano, fetchStatusPlano, planoTipo } = usePlano();

    const CTA_GREEN_PLANOS = "#128C7E";
    const CTA_GREEN_PLANOS_HOVER = "#0E6F64";

    const CTA_GREEN_HIGHLIGHT = "#1BC5B1";
    const CTA_GREEN_HIGHLIGHT_HOVER = "#17B2A2";

    // Drawer (mobile)
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Modal de escolha de plano

    const [aderindo, setAderindo] = useState(false);

    const [reuseOpen, setReuseOpen] = useState(false);
    const [reuseInfo, setReuseInfo] = useState(null);
    const [reusePlan, setReusePlan] = useState(null);

    const [pixOpen, setPixOpen] = useState(false);
    const [pixData, setPixData] = useState(null);
    const [pendingPixPayment, setPendingPixPayment] = useState(null);
    const lastAutoOpenedPixPaymentRef = useRef(null);

    const [couponInfo, setCouponInfo] = useState({
        loading: false,
        valid: false,
        descontoPercentual: 0,
        referralCode: "",
        eligible: false,
    });

    const { showSnackbar } = useSnackbar();

    useEffect(() => {
        if (user?.id) fetchStatusPlano(user.id);
    }, [user, fetchStatusPlano]);

    useEffect(() => {
        if (!user?.id || planoStatus !== 'P') {
            setPendingPixPayment(null);
            return;
        }

        let active = true;

        async function fetchPendingPixPayment() {
            try {
                const res = await api.get('/billing/plan');
                if (!active) return;

                const plan = normalizePlan(res.data);
                const isPixPending =
                    String(plan?.status || '').toUpperCase() === 'P' &&
                    String(plan?.paymentMethod || '').toUpperCase() === 'PIX' &&
                    !!plan?.pixPayment;

                if (!isPixPending) {
                    setPendingPixPayment(null);
                    return;
                }

                const dialogData = normalizePixDialogData({ ...plan.pixPayment, planName: plan.planName || plan.plan }, plan.planName || plan.plan);
                setPendingPixPayment(dialogData);

                if (dialogData.paymentId && lastAutoOpenedPixPaymentRef.current !== dialogData.paymentId) {
                    lastAutoOpenedPixPaymentRef.current = dialogData.paymentId;
                    setPixData(dialogData);
                    setPixOpen(true);
                }
            } catch {
                if (active) setPendingPixPayment(null);
            }
        }

        fetchPendingPixPayment();

        return () => {
            active = false;
        };
    }, [user?.id, planoStatus]);

    // Fluxo novo: abrir modal de planos
    const { isPlanModalOpen: planModalOpen, openPlanModal, closePlanModal } = usePlanModal();

    useEffect(() => {
        if (!planModalOpen) return;

        const referralCode = String(user?.referralCode || "").trim();

        if (!referralCode) {
            setCouponInfo({
                loading: false,
                valid: false,
                descontoPercentual: 0,
                referralCode: "",
                eligible: false,
            });
            return;
        }

        let active = true;

        async function fetchInternalCoupon() {
            setCouponInfo({
                loading: true,
                valid: false,
                descontoPercentual: 0,
                referralCode,
                eligible: false,
            });

            try {
                const [planRes, couponRes] = await Promise.all([
                    api.get("/billing/plan"),
                    api.get(`/partner/cupom/${encodeURIComponent(referralCode)}`),
                ]);

                if (!active) return;

                const planInfo = normalizePlan(planRes.data);
                const firstSubscriptionEligible =
                    String(planInfo?.status || "").toUpperCase() === "G" &&
                    !planInfo?.subscribedAt &&
                    !planInfo?.externalSubscriptionId;
                const descontoPercentual = Number(couponRes.data?.couponDiscountPercentage || 0);
                const validCoupon = !!couponRes.data?.valid && descontoPercentual > 0;

                setCouponInfo({
                    loading: false,
                    valid: firstSubscriptionEligible && validCoupon,
                    descontoPercentual: firstSubscriptionEligible && validCoupon ? descontoPercentual : 0,
                    referralCode,
                    eligible: firstSubscriptionEligible,
                });
            } catch {
                if (!active) return;

                setCouponInfo({
                    loading: false,
                    valid: false,
                    descontoPercentual: 0,
                    referralCode,
                    eligible: false,
                });
            }
        }

        fetchInternalCoupon();

        return () => {
            active = false;
        };
    }, [planModalOpen, user?.referralCode]);

    const hasFirstSubscriptionDiscount =
        couponInfo.valid && couponInfo.eligible && couponInfo.descontoPercentual > 0;

    // ===== Dialog Cartão (checkout embutido) =====
    const [cardOpen, setCardOpen] = useState(false);
    const [cardPlan, setCardPlan] = useState(null);
    const [cardForm, setCardForm] = useState({
        number: "",
        name: "",
        expiry: "",
        cvv: ""
    });

    const digits = (v = "") => String(v).replace(/\D/g, "");

    const CARD_RULES = {
        amex: { max: 15, groups: [4, 6, 5] },
        diners: { max: 14, groups: [4, 6, 4] },
        default: { max: 16, groups: [4, 4, 4, 4] },
    };

    const detectBrand = (num) => {
        const n = digits(num);

        // Amex
        if (/^3[47]/.test(n)) return "amex";

        // Visa
        if (/^4/.test(n)) return "visa";

        // Mastercard (prefixos modernos)
        if (/^(5[1-5]|2[2-7])/.test(n)) return "mastercard";

        // Hipercard
        if (/^(606282|3841)/.test(n)) return "hipercard";

        // Elo (principais BINs)
        if (/^(4011|4312|4389|4514|4576|5041|5066|5067|5090|6277|6362|6363)/.test(n)) return "elo";

        // Diners (14 dígitos)
        if (/^(36|30[0-5]|38|39)/.test(n)) return "diners";

        // Discover
        if (/^(6011|65|64[4-9]|622)/.test(n)) return "discover";

        // JCB
        if (/^35/.test(n)) return "jcb";

        return null;
    };

    const brand = detectBrand(cardForm.number);

    const formatCardNumber = (v) => {
        const rawDigits = digits(v);

        const b = detectBrand(rawDigits);
        const rule = CARD_RULES[b] || CARD_RULES.default;

        // NUNCA deixa passar do tamanho máximo (15/14/16)
        const d = rawDigits.slice(0, rule.max);

        // monta grupos com espaços conforme regra
        const parts = [];
        let i = 0;
        for (const size of rule.groups) {
            if (i >= d.length) break;
            parts.push(d.slice(i, i + size));
            i += size;
        }
        if (i < d.length) parts.push(d.slice(i));

        return parts.join(" ");
    };


    const formatExpiry = (v) => {
        const d = digits(v).slice(0, 4);
        if (d.length <= 2) return d;
        return `${d.slice(0, 2)}/${d.slice(2)}`;
    };

    const onlyLetters = (v) =>
        String(v)
            .replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g, "")
            .replace(/\s{2,}/g, " ");

    const openCardDialog = (tipoPlano) => {
        setCardPlan(tipoPlano);
        setCardForm({ number: "", name: "", expiry: "", cvv: "" });
        setCardOpen(true);
    };

    const closeCardDialog = () => {
        if (aderindo) return;
        setCardOpen(false);
    };

    // Após selecionar plano: mantém o fluxo antigo, mas com os endpoints REST novos.
    // 1) consulta se há cartão salvo; 2) reutiliza o cartão ou abre o Dialog interno.
    const handleEscolherPlano = async (tipoPlano) => {
        closePlanModal();
        setAderindo(true);

        try {
            const res = await api.get("/billing/plan");
            const planInfo = normalizePlan(res.data);

            const info = {
                ...planInfo,
                hasCard: Boolean(planInfo?.creditCardToken || planInfo?.creditCardLast4),
                brand: planInfo?.creditCardBrand,
                last4: planInfo?.creditCardLast4,
            };

            if (info.hasCard) {
                setReusePlan(tipoPlano);
                setReuseInfo(info);
                setReuseOpen(true);
                return;
            }

            openCardDialog(tipoPlano);
        } catch (err) {
            // Se a consulta do plano falhar, ainda permite o fluxo normal de cartão,
            // igual ao comportamento antigo.
            openCardDialog(tipoPlano);
        } finally {
            setAderindo(false);
        }
    };

    const handleEscolherPixPlano = async (tipoPlano) => {
        closePlanModal();
        setAderindo(true);

        try {
            const res = await api.post('/billing/subscription/pix', {
                planName: tipoPlano,
            });

            const dialogData = normalizePixDialogData(res.data, tipoPlano);
            setPixData(dialogData);
            setPendingPixPayment(dialogData);
            setPixOpen(true);
            showSnackbar('PIX mensal gerado. Pague para liberar o plano.', 'info');

            if (user?.id) fetchStatusPlano(user.id);
        } catch (err) {
            showSnackbar(getApiErrorMessage(err, 'Não foi possível gerar o PIX mensal. Tente novamente.'), 'error');
        } finally {
            setAderindo(false);
        }
    };

    const handleOpenPendingPix = () => {
        if (!pendingPixPayment) return;
        setPixData(pendingPixPayment);
        setPixOpen(true);
    };

    const handlePixPaid = () => {
        setPendingPixPayment(null);
        setPixOpen(false);
        if (user?.id) fetchStatusPlano(user.id);
    };

    const handleConfirmarReuse = async () => {
        if (!reusePlan) return;

        setAderindo(true);
        try {
            await api.post("/billing/subscription/credit-card/token", {
                planName: reusePlan,
            });

            showSnackbar("Assinatura criada com sucesso!", "success");
            setReuseOpen(false);
            if (user?.id) fetchStatusPlano(user.id);
            return;
        } catch (err) {
            setReuseOpen(false);
            openCardDialog(reusePlan);
            showSnackbar(
                getApiErrorMessage(err, "Seu cartão salvo não pôde ser reutilizado. Informe o cartão novamente."),
                "warning"
            );
        } finally {
            setAderindo(false);
        }
    };

    const handleConfirmarCartao = async () => {
        if (!cardPlan) return;

        const num = digits(cardForm.number);
        const exp = digits(cardForm.expiry);
        const cvv = digits(cardForm.cvv);

        if (!cardForm.name || !num || !exp || !cvv) {
            showSnackbar("Preencha todos os dados do cartão.", "warning");
            return false;
        }

        const b = detectBrand(num);
        const rule = CARD_RULES[b] || CARD_RULES.default;

        if (num.length !== rule.max) return showSnackbar("Número do cartão inválido.", "warning");

        if (cardForm.name.trim().length < 3) return showSnackbar("Nome do titular inválido.", "warning");
        if (exp.length !== 4) return showSnackbar("Validade inválida.", "warning");

        // CVV: Amex = 4, demais = 3
        const cvvNeeded = b === "amex" ? 4 : 3;
        if (cvv.length !== cvvNeeded) return showSnackbar(`CVV inválido. Use ${cvvNeeded} dígitos.`, "warning");

        const expiryMonth = exp.slice(0, 2);
        const expiryYear = `20${exp.slice(2)}`; // mm/aa -> 20aa

        setAderindo(true);
        try {
            await api.post("/billing/subscription/credit-card", {
                planName: cardPlan,
                creditCard: {
                    holderName: cardForm.name.trim(),
                    number: num,
                    expiryMonth,
                    expiryYear,
                    ccv: cvv,
                },
            });

            showSnackbar("Assinatura criada com sucesso!", "success");
            setCardOpen(false);
            closePlanModal();
            if (user?.id) fetchStatusPlano(user.id);
            return;
        } catch (err) {
            showSnackbar(getApiErrorMessage(err, "Não foi possível concluir a assinatura. Tente novamente."), "error");
        } finally {
            setAderindo(false);
        }
    };

    const statusConfig = planoStatus ? STATUS_CONFIG[planoStatus] : null;

    const remainingGenerations = Number(restantes)
    const remainingImageGenerations = Number(imagensRestantes)

    const showRemainingGenerations =
        planoStatus && ["A", "B"].includes(planoStatus) && Number.isFinite(remainingGenerations) && remainingGenerations >= 0
    const showRemainingImageGenerations =
        planoStatus && ["A", "B"].includes(planoStatus) && Number.isFinite(remainingImageGenerations) && remainingImageGenerations >= 0

    const remainingUsageItems = [
        showRemainingGenerations ? `${remainingGenerations} doc${remainingGenerations === 1 ? "" : "s"}` : null,
        showRemainingImageGenerations ? `${remainingImageGenerations} img${remainingImageGenerations === 1 ? "" : "s"}` : null,
    ].filter(Boolean)
    const remainingUsageText = remainingUsageItems.length
        ? `Restam: ${remainingUsageItems.join(" • ")}${isMobile ? "" : " neste mês"}`
        : ""

    // STATUS CHIP - DESKTOP
    const StatusChipDesktop = loadingPlano ? (
        <CircularProgress size={22} />
    ) : planoStatus && statusConfig ? (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 0.4,
            }}
        >
            <Tooltip title={statusConfig.tooltip} arrow>
                <Chip
                    label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {statusConfig.icon}

                            <span>
                                {statusConfig.label}
                                {["A"].includes(planoStatus) && planoTipo
                                    ? ` - ${planoTipo.charAt(0).toUpperCase() + planoTipo.slice(1).toLowerCase()}`
                                    : ""}
                            </span>
                        </Box>
                    }
                    color={statusConfig.color}
                    sx={{
                        fontWeight: 600,
                        fontSize: 15,
                        px: 2,
                        py: "2px",
                        borderRadius: 2,
                        boxShadow:
                            theme.palette.mode === "dark"
                                ? "0 0 8px #2224"
                                : "0 2px 8px #ccd7",
                        textTransform: "none",
                        cursor: "default",
                    }}
                    variant="outlined"
                />
            </Tooltip>

            {(showRemainingGenerations || showRemainingImageGenerations) && (
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: 10,
                        fontWeight: 500,
                        lineHeight: 1,
                        color: remainingGenerations <= 2 || remainingImageGenerations <= 2 ? "warning.main" : "text.secondary",
                        opacity: 0.85,
                        pr: 0.5,
                    }}
                >
                    {remainingUsageText}
                </Typography>
            )}
        </Box>
    ) : null

    // STATUS CHIP - MOBILE
    const StatusChipMobile = loadingPlano ? (
        <CircularProgress size={22} />
    ) : planoStatus && statusConfig ? (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.35,
                maxWidth: "100%",
            }}
        >
            <Tooltip title={statusConfig.tooltip} arrow>
                <Chip
                    size={isMobile ? "small" : "medium"}
                    label={
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: isMobile ? 0.75 : 1,
                                minWidth: 0,
                            }}
                        >
                            {React.cloneElement(statusConfig.icon, {
                                sx: {
                                    ...(statusConfig.icon?.props?.sx || {}),
                                    fontSize: isMobile ? 18 : 20,
                                },
                            })}

                            <Box
                                component="span"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: isMobile ? 12.5 : 15,
                                    minWidth: 0,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {statusConfig.label}
                                {["A"].includes(planoStatus) && planoTipo
                                    ? ` - ${planoTipo.charAt(0).toUpperCase() +
                                    planoTipo.slice(1).toLowerCase()
                                    }`
                                    : ""}
                            </Box>
                        </Box>
                    }
                    color={statusConfig.color}
                    variant="outlined"
                    sx={{
                        fontWeight: 800,
                        borderRadius: 999,
                        px: isMobile ? 1.25 : 2,
                        py: isMobile ? "1px" : "2px",
                        boxShadow: "none",
                        cursor: "default",
                        bgcolor: "transparent",
                        maxWidth: "100%",
                    }}
                />
            </Tooltip>

            {(showRemainingGenerations || showRemainingImageGenerations) && (
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: 10,
                        fontWeight: 500,
                        lineHeight: 1.1,
                        color: remainingGenerations <= 2 || remainingImageGenerations <= 2 ? "warning.main" : "text.secondary",
                        opacity: 0.85,
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {remainingUsageText}
                </Typography>
            )}
        </Box>
    ) : null

    const StatusChip = isMobile ? StatusChipMobile : StatusChipDesktop;

    // BOTÃO DE ADESÃO: agora abre modal
    const PlanoButton = statusConfig?.action && (
        <Button
            sx={{
                ml: isMobile ? 0 : 1,
                px: isMobile ? 1.25 : 2.8,
                minWidth: isMobile ? 92 : "unset",
                fontWeight: 800,
                letterSpacing: 0.2,
                background: "linear-gradient(90deg, #3b82f6 30%, #7f72f7 90%)",
                color: "#fff",
                borderRadius: 2,
                boxShadow: isMobile ? "none" : "0 2px 8px #ccf5",
                whiteSpace: "nowrap",
                '&:hover': {
                    background: "linear-gradient(90deg, #456ff2 30%, #6548da 90%)"
                }
            }}
            size={isMobile ? "small" : "small"}
            onClick={openPlanModal}
            disabled={aderindo}
        >
            {aderindo ? <CircularProgress size={18} sx={{ color: "white" }} /> : statusConfig.labelButton}
        </Button>
    );

    const PendingPixButton = pendingPixPayment && planoStatus === 'P' && (
        <Button
            size={isMobile ? "small" : "small"}
            variant="contained"
            startIcon={<PixIcon />}
            onClick={handleOpenPendingPix}
            sx={{
                ml: isMobile ? 0 : 1,
                px: isMobile ? 1.25 : 2.4,
                minWidth: isMobile ? 92 : "unset",
                fontWeight: 900,
                borderRadius: 2,
                whiteSpace: "nowrap",
                bgcolor: "success.main",
                color: "success.contrastText",
                '&:hover': { bgcolor: "success.dark" },
            }}
        >
            Pagar PIX
        </Button>
    );

    // MODAL DE ESCOLHA DE PLANO
    const PlanModal = (
        <Dialog
            open={planModalOpen}
            onClose={closePlanModal}
            fullScreen={isMobile}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: (theme) => ({
                    p: 0,
                    overflow: "hidden",
                    borderRadius: isMobile ? 0 : 5,
                    border: isMobile
                        ? "none"
                        : `1px solid ${alpha(theme.palette.divider, 0.65)}`,
                    backgroundColor: theme.palette.background.paper,
                    boxShadow:
                        theme.palette.mode === "dark"
                            ? `0 24px 80px ${alpha("#000", 0.55)}`
                            : `0 24px 80px ${alpha("#4f46e5", 0.18)}`,
                }),
            }}
        >
            <Box
                sx={(theme) => ({
                    position: "relative",
                    px: isMobile ? 2.2 : 4,
                    pt: isMobile ? 3.5 : 4,
                    pb: isMobile ? 2 : 2.5,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
                })}
            >
                <IconButton
                    aria-label="close"
                    onClick={closePlanModal}
                    disabled={aderindo}
                    sx={(theme) => ({
                        position: "absolute",
                        right: isMobile ? 10 : 16,
                        top: isMobile ? 10 : 16,
                        width: 38,
                        height: 38,
                        borderRadius: 3,
                        color: "text.secondary",
                        bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.08 : 0.05),
                        "&:hover": {
                            bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.14 : 0.09),
                        },
                    })}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                <Stack spacing={1.1} alignItems="center" sx={{ textAlign: "center" }}>
                    <Chip
                        size="small"
                        label="Assinatura mensal"
                        sx={(theme) => ({
                            display: "none",
                            height: 26,
                            fontWeight: 800,
                            color: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.09),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                        })}
                    />

                    <DialogTitle
                        sx={{
                            p: 0,
                            fontWeight: 950,
                            fontSize: isMobile ? 22 : 30,
                            letterSpacing: -0.6,
                            color: "text.primary",
                            lineHeight: 1.12,
                        }}
                    >
                        O plano ideal para você
                    </DialogTitle>

                </Stack>

                {(couponInfo.loading || hasFirstSubscriptionDiscount) && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2, px: 1 }}>
                        {couponInfo.loading ? (
                            <Chip
                                size="small"
                                label="Verificando cupom de parceiro..."
                                sx={(theme) => ({
                                    fontWeight: 850,
                                    bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.08 : 0.05),
                                })}
                            />
                        ) : (
                            <Chip
                                size="small"
                                color="success"
                                icon={<DiscountIcon fontSize="small" />}
                                label={`Cupom aplicado • ${couponInfo.descontoPercentual}% OFF na 1ª mensalidade`}
                                sx={{
                                    fontWeight: 900,
                                    maxWidth: "100%",
                                    borderRadius: 999,
                                    "& .MuiChip-label": {
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    },
                                }}
                            />
                        )}
                    </Box>
                )}
            </Box>

            <DialogContent
                sx={{
                    px: isMobile ? 0 : 3,
                    py: isMobile ? 2 : 3,
                    overflowX: "hidden",
                }}
            >
                <Grid
                    container
                    spacing={isMobile ? 0 : 2.6}
                    justifyContent="center"
                    alignItems="stretch"
                    sx={{
                        maxWidth: 1120,
                        width: "100%",
                        mx: "auto",
                        ...(isMobile && {
                            px: 2,
                            gap: 2,
                            flexDirection: "column",
                            alignItems: "center",
                        }),
                    }}
                >
                    {PLANS.map((plan) => {
                        const isHighlighted = plan.highlight;
                        const valorOriginal = parseBRLToNumber(plan.price);
                        const valorComDesconto = hasFirstSubscriptionDiscount
                            ? calcDiscountedPrice(valorOriginal, couponInfo.descontoPercentual)
                            : valorOriginal;

                        return (
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                key={plan.tipo}
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    ...(isMobile && {
                                        width: "100%",
                                        maxWidth: 430,
                                        flexBasis: "auto !important",
                                        paddingLeft: "0 !important",
                                        paddingTop: "0 !important",
                                    }),
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={(theme) => {
                                        const lilac = "#7f72f7";
                                        const isDark = theme.palette.mode === "dark";

                                        return {
                                            width: "100%",
                                            height: "100%",
                                            p: isMobile ? 2.2 : 2.6,
                                            display: "flex",
                                            flexDirection: "column",
                                            position: "relative",
                                            borderRadius: 4.5,
                                            overflow: "visible",
                                            border: isHighlighted
                                                ? `1.5px solid ${alpha(lilac, isDark ? 0.65 : 0.75)}`
                                                : `1px solid ${alpha(theme.palette.divider, 0.72)}`,
                                            background: isHighlighted
                                                ? isDark
                                                    ? `linear-gradient(180deg, ${alpha(lilac, 0.18)} 0%, ${alpha(theme.palette.background.paper, 0.96)} 46%)`
                                                    : `linear-gradient(180deg, ${alpha(lilac, 0.13)} 0%, #ffffff 48%)`
                                                : isDark
                                                    ? alpha(theme.palette.background.paper, 0.78)
                                                    : "#ffffff",
                                            boxShadow: isHighlighted
                                                ? `0 18px 50px ${alpha(lilac, isDark ? 0.22 : 0.20)}`
                                                : `0 14px 36px ${alpha(theme.palette.common.black, isDark ? 0.22 : 0.065)}`,
                                            transform: isHighlighted && !isMobile ? "translateY(-4px)" : "none",
                                            transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                                            "&:hover": {
                                                transform: isMobile
                                                    ? "none"
                                                    : isHighlighted
                                                        ? "translateY(-7px)"
                                                        : "translateY(-5px)",
                                                boxShadow: isHighlighted
                                                    ? `0 22px 58px ${alpha(lilac, isDark ? 0.28 : 0.24)}`
                                                    : `0 18px 46px ${alpha(theme.palette.common.black, isDark ? 0.28 : 0.095)}`,
                                            },
                                        };
                                    }}
                                >
                                    {isHighlighted ?

                                        <Chip
                                            label={plan.badge}
                                            size="small"
                                            sx={(theme) => ({
                                                position: "absolute",
                                                top: -13,
                                                left: "50%",
                                                transform: "translateX(-50%)",
                                                height: 27,
                                                px: 0.5,
                                                fontWeight: 950,
                                                color: "#fff",
                                                bgcolor: "#7f72f7",
                                                boxShadow: `0 10px 22px ${alpha("#7f72f7", 0.32)}`,
                                                "& .MuiChip-label": {
                                                    px: 1.4,
                                                },
                                            })}
                                        />

                                        :

                                        <Chip
                                            label={plan.badge}
                                            size="small"
                                            sx={(theme) => ({
                                                position: "absolute",
                                                top: -13,
                                                left: "50%",
                                                transform: "translateX(-50%)",
                                                height: 27,
                                                px: 0.5,
                                                fontWeight: 950,
                                                color: "#fff",
                                                bgcolor: plan.highlight
                                                    ? theme.palette.primary.main
                                                    : theme.palette.grey[600],
                                                boxShadow: plan.highlight
                                                    ? `0 10px 22px ${alpha(theme.palette.primary.main, 0.32)}`
                                                    : `0 10px 22px ${alpha(theme.palette.grey[600], 0.25)}`,
                                                "& .MuiChip-label": {
                                                    px: 1.4,
                                                },
                                            })}
                                        />

                                    }

                                    <Stack spacing={1.25} alignItems="center" sx={{ textAlign: "center" }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 950,
                                                color: isHighlighted ? "primary.main" : "text.primary",
                                                letterSpacing: -0.25,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {plan.title}
                                        </Typography>

                                        <Box>
                                            {!hasFirstSubscriptionDiscount && (
                                                <Typography
                                                    sx={(theme) => ({
                                                        fontSize: 13,
                                                        fontWeight: 850,
                                                        textDecoration: "line-through",
                                                        color: alpha(
                                                            theme.palette.text.primary,
                                                            theme.palette.mode === "dark" ? 0.5 : 0.42
                                                        ),
                                                        lineHeight: 1.1,
                                                    })}
                                                >
                                                    {plan.priceDisabled}
                                                </Typography>
                                            )}

                                            <Typography
                                                sx={(theme) => ({
                                                    mt: 0.25,
                                                    fontSize: isMobile ? 27 : 30,
                                                    fontWeight: 950,
                                                    letterSpacing: -1,
                                                    color: theme.palette.mode === "light" ? "#272a35" : "#f8fafc",
                                                    lineHeight: 1.05,
                                                })}
                                            >
                                                {hasFirstSubscriptionDiscount
                                                    ? `${formatBRL(valorComDesconto)}/mês`
                                                    : plan.price}
                                            </Typography>

                                            <Typography sx={{ color: "text.secondary", fontSize: 13, lineHeight: 2 }}>
                                                {plan.desc}
                                            </Typography>

                                            {hasFirstSubscriptionDiscount && (
                                                <>
                                                    <Typography
                                                        sx={{
                                                            mt: 0.7,
                                                            fontSize: 12.5,
                                                            color: "text.secondary",
                                                            textDecoration: "line-through",
                                                        }}
                                                    >
                                                        1ª mensalidade sem cupom: {formatBRL(valorOriginal)}
                                                    </Typography>

                                                    <Chip
                                                        label="Cupom aplicado"
                                                        color="success"
                                                        size="small"
                                                        sx={{
                                                            mt: 1,
                                                            height: 24,
                                                            fontWeight: 850,
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </Box>

                                        {IS_PROMO && (
                                            <Box
                                                sx={(theme) => ({
                                                    width: "100%",
                                                    mt: 0.6,
                                                    p: 1.15,
                                                    borderRadius: 3,
                                                    bgcolor: alpha(theme.palette.error.main, theme.palette.mode === "dark" ? 0.10 : 0.065),
                                                    border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
                                                })}
                                            >
                                                <Chip
                                                    size="small"
                                                    label="Oferta Especial"
                                                    color="error"
                                                    icon={<CelebrationIcon sx={{ fontSize: 14 }} />}
                                                    sx={{
                                                        height: 24,
                                                        fontWeight: 850,
                                                        borderRadius: 999,
                                                    }}
                                                />

                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: "block",
                                                        mt: 0.85,
                                                        fontSize: 12,
                                                        color: "text.secondary",
                                                        lineHeight: 1.35,
                                                    }}
                                                >
                                                    <ReportProblemIcon
                                                        sx={{
                                                            fontSize: 16,
                                                            verticalAlign: "middle",
                                                            color: "warning.main",
                                                            mr: 0.35,
                                                        }}
                                                    />
                                                    2 primeiros meses{" "}
                                                    <Box component="span" sx={{ fontWeight: 850 }}>
                                                        grátis
                                                    </Box>
                                                    . Promoção válida até{" "}
                                                    <Box component="span" sx={{ fontWeight: 850 }}>
                                                        31/12/2025
                                                    </Box>
                                                    .
                                                </Typography>

                                                <PromoCountdown />
                                            </Box>
                                        )}
                                    </Stack>

                                    <Divider
                                        sx={(theme) => ({

                                            my: 2.2,
                                            borderColor: alpha(theme.palette.divider, 0.72),
                                        })}
                                    />

                                    <Stack spacing={1.05} sx={{ mb: 2.2, flexGrow: 1 }}>
                                        {(() => {
                                            const isProfessor = plan.tipo === "PROFESSOR";
                                            const isMentor = plan.tipo === "MENTOR";

                                            const WHATSAPP = "Suporte por WhatsApp";

                                            const extrasRiscados = isProfessor
                                                ? PROFESSOR_EXTRAS_RISCADOS
                                                : isMentor
                                                    ? MENTOR_EXTRAS_RISCADOS
                                                    : [];

                                            const baseSemWhatsapp = plan.features.filter((f) => f !== WHATSAPP);
                                            const whatsappNoFinal = plan.features.includes(WHATSAPP) ? [WHATSAPP] : [];

                                            const featuresToRender =
                                                isProfessor || isMentor
                                                    ? [...baseSemWhatsapp, ...extrasRiscados, ...whatsappNoFinal]
                                                    : plan.features;

                                            return featuresToRender.map((feature, i) => {
                                                const isRiscada =
                                                    (isProfessor || isMentor) && extrasRiscados.includes(feature);

                                                const match = feature.match(/^(\d+)(.*)$/);

                                                return (
                                                    <Box
                                                        key={`${feature}-${i}`}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <CheckCircleRoundedIcon
                                                            sx={(theme) => ({
                                                                mt: "2px",
                                                                fontSize: 18,
                                                                color: isRiscada
                                                                    ? "text.disabled"
                                                                    : isHighlighted
                                                                        ? theme.palette.primary.main
                                                                        : theme.palette.success.main,
                                                            })}
                                                        />

                                                        <Typography
                                                            sx={{
                                                                fontSize: 14.2,
                                                                lineHeight: 1.42,
                                                                color: isRiscada ? "text.disabled" : "text.secondary",
                                                                textDecoration: isRiscada ? "line-through" : "none",
                                                            }}
                                                        >
                                                            {match ? (
                                                                <>
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            fontWeight: 900,
                                                                            color: isRiscada ? "text.disabled" : "text.primary",
                                                                            textDecoration: isRiscada ? "line-through" : "underline",
                                                                        }}
                                                                    >
                                                                        {match[1]}
                                                                    </Box>
                                                                    {match[2]}
                                                                </>
                                                            ) : (
                                                                feature
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                );
                                            });
                                        })()}
                                    </Stack>

                                    <Stack spacing={1.05} sx={{ mt: "auto" }}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            disableElevation
                                            sx={(theme) => ({
                                                width: "100%",
                                                minHeight: 58,
                                                px: 1.35,
                                                py: 1.1,
                                                borderRadius: 3.2,
                                                justifyContent: "flex-start",
                                                textTransform: "none",
                                                bgcolor: isHighlighted ? CTA_GREEN_HIGHLIGHT : CTA_GREEN_PLANOS,
                                                background: isHighlighted
                                                    ? `linear-gradient(135deg, ${CTA_GREEN_HIGHLIGHT} 0%, ${CTA_GREEN_HIGHLIGHT_HOVER} 100%)`
                                                    : `linear-gradient(135deg, ${CTA_GREEN_PLANOS} 0%, ${CTA_GREEN_PLANOS_HOVER} 100%)`,
                                                boxShadow: `0 12px 24px ${alpha(
                                                    isHighlighted ? CTA_GREEN_HIGHLIGHT : CTA_GREEN_PLANOS,
                                                    theme.palette.mode === "dark" ? 0.22 : 0.28
                                                )}`,
                                                "&:hover": {
                                                    bgcolor: isHighlighted ? CTA_GREEN_HIGHLIGHT_HOVER : CTA_GREEN_PLANOS_HOVER,
                                                    boxShadow: `0 14px 28px ${alpha(
                                                        isHighlighted ? CTA_GREEN_HIGHLIGHT : CTA_GREEN_PLANOS,
                                                        theme.palette.mode === "dark" ? 0.28 : 0.34
                                                    )}`,
                                                    transform: "translateY(-1px)",
                                                },
                                                "&:disabled": {
                                                    color: alpha("#fff", 0.85),
                                                    opacity: 0.72,
                                                },
                                            })}
                                            disabled={aderindo}
                                            onClick={() => handleEscolherPlano(plan.tipo)}
                                        >
                                            {aderindo ? (
                                                <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                                                    <CircularProgress size={21} sx={{ color: "white" }} />
                                                </Box>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1.15,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 35,
                                                            height: 35,
                                                            borderRadius: 2.2,
                                                            display: "grid",
                                                            placeItems: "center",
                                                            bgcolor: alpha("#fff", 0.16),
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <CreditCardRoundedIcon sx={{ fontSize: 20 }} />
                                                    </Box>

                                                    <Box sx={{ textAlign: "left", flexGrow: 1, minWidth: 0 }}>
                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                display: "block",
                                                                fontSize: 15,
                                                                fontWeight: 950,
                                                                lineHeight: 1.1,
                                                            }}
                                                        >
                                                            Cartão de crédito
                                                        </Typography>

                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                display: "block",
                                                                mt: 0.25,
                                                                fontSize: 11.5,
                                                                fontWeight: 650,
                                                                opacity: 0.88,
                                                                lineHeight: 1.1,
                                                            }}
                                                        >
                                                            Liberação imediata
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            size="large"
                                            sx={(theme) => ({
                                                width: "100%",
                                                minHeight: 56,
                                                px: 1.35,
                                                py: 1.05,
                                                borderRadius: 3.2,
                                                justifyContent: "flex-start",
                                                textTransform: "none",
                                                borderWidth: 1.4,
                                                borderColor: alpha(theme.palette.success.main, 0.48),
                                                color: theme.palette.success.main,
                                                bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.08 : 0.045),
                                                "&:hover": {
                                                    borderWidth: 1.4,
                                                    borderColor: theme.palette.success.main,
                                                    bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.14 : 0.08),
                                                    transform: "translateY(-1px)",
                                                },
                                                "&:disabled": {
                                                    opacity: 0.65,
                                                },
                                            })}
                                            disabled={aderindo}
                                            onClick={() => handleEscolherPixPlano(plan.tipo)}
                                        >
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1.15,
                                                }}
                                            >
                                                <Box
                                                    sx={(theme) => ({
                                                        width: 35,
                                                        height: 35,
                                                        borderRadius: 2.2,
                                                        display: "grid",
                                                        placeItems: "center",
                                                        bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.16 : 0.10),
                                                        flexShrink: 0,
                                                    })}
                                                >
                                                    <PixIcon sx={{ fontSize: 20 }} />
                                                </Box>

                                                <Box sx={{ textAlign: "left", flexGrow: 1, minWidth: 0 }}>
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            display: "block",
                                                            fontSize: 15,
                                                            fontWeight: 950,
                                                            lineHeight: 1.1,
                                                        }}
                                                    >
                                                        PIX mensal
                                                    </Typography>

                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            display: "block",
                                                            mt: 0.25,
                                                            fontSize: 11.5,
                                                            fontWeight: 650,
                                                            color: "text.secondary",
                                                            lineHeight: 1.1,
                                                        }}
                                                    >
                                                        Libera após confirmação
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Button>

                                        <Typography
                                            variant="caption"
                                            sx={(theme) => ({
                                                mt: 0.25,
                                                px: 1,
                                                py: 0.85,
                                                borderRadius: 2.5,
                                                color: "text.secondary",
                                                textAlign: "center",
                                                lineHeight: 1.28,
                                                bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.055 : 0.035),
                                            })}
                                        >
                                            {hasFirstSubscriptionDiscount
                                                ? "Cupom aplicado no cartão. PIX mensal usa o valor padrão do plano."
                                                : "Cartão libera na hora. PIX libera após confirmação do pagamento."}
                                        </Typography>
                                    </Stack>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            </DialogContent>

            <DialogActions
                sx={(theme) => ({
                    justifyContent: "center",
                    px: isMobile ? 2 : 3,
                    py: isMobile ? 1.6 : 2.2,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.58)}`,
                    bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.035 : 0.018),
                })}
            >
                <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <Typography
                        variant="body2"
                        sx={{
                            width: "100%",
                            maxWidth: 620,
                            color: "text.secondary",
                            textAlign: "center",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.75,
                            flexWrap: "wrap",
                            lineHeight: 1.35,
                            fontSize: 13,
                        }}
                    >
                        <AssuredWorkloadRoundedIcon sx={{ fontSize: 17, opacity: 0.85 }} aria-hidden />
                        <Box component="span">Cobrança em nome de:</Box>
                        <Box
                            component="strong"
                            sx={{
                                fontWeight: 900,
                                color: "text.primary",
                                whiteSpace: "nowrap",
                            }}
                        >
                            EdenTech Soluções Tecnológicas
                        </Box>
                    </Typography>
                </Box>
            </DialogActions>
        </Dialog>
    );

    // MODAL DE INSERIÇÃO DE CARTÃO
    const CardDialog = (
        <Dialog open={cardOpen} onClose={closeCardDialog} fullWidth maxWidth="xs" fullScreen={isMobile} PaperProps={{
            sx: {
                p: isMobile ? 0 : 1,
                borderRadius: isMobile ? 0 : 3
            }
        }}>
            <DialogTitle sx={{ fontWeight: 800 }}>
                {PLAN_LABEL[cardPlan] || "Plano selecionado"}
            </DialogTitle>


            <IconButton
                aria-label="close"
                onClick={closeCardDialog}
                disabled={aderindo}
                sx={(theme) => ({ position: "absolute", right: 8, top: 8, color: theme.palette.grey[500] })}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ pt: 1 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                    {["visa", "mastercard", "elo", "diners", "discover", "amex", "hipercard", "jcb"].map((b) => (
                        <Box
                            key={b}
                            component="img"
                            alt={b}
                            src={`/cards/${b}.svg`}
                            sx={{ height: 20, opacity: brand && brand !== b ? 0.35 : 1 }}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                    ))}
                </Box>

                <TextField
                    label="Número do cartão"
                    value={cardForm.number}
                    onChange={(e) => setCardForm(v => ({ ...v, number: formatCardNumber(e.target.value) }))}
                    fullWidth
                    margin="dense"
                    inputMode="numeric"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                    {brand ? brand.toUpperCase() : ""}
                                </Typography>
                            </InputAdornment>
                        )
                    }}
                    required
                />

                <TextField
                    label="Nome do titular"
                    value={cardForm.name}
                    onChange={(e) => setCardForm(v => ({ ...v, name: onlyLetters(e.target.value) }))}
                    fullWidth
                    margin="dense"
                    required
                />

                <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField
                        label="Validade (MM/AA)"
                        value={cardForm.expiry}
                        onChange={(e) => setCardForm(v => ({ ...v, expiry: formatExpiry(e.target.value) }))}
                        margin="dense"
                        inputMode="numeric"
                        fullWidth
                        required
                    />
                    <TextField
                        label="CVV"
                        value={cardForm.cvv}
                        onChange={(e) => {
                            const b = detectBrand(cardForm.number);
                            const max = b === "amex" ? 4 : 3;
                            setCardForm(v => ({ ...v, cvv: digits(e.target.value).slice(0, max) }));
                        }}
                        margin="dense"
                        inputMode="numeric"
                        fullWidth
                        required
                    />
                </Box>

                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                    Seus dados estão tokenizados e seguros. O cartão não fica salvo em texto na plataforma.
                </Typography>

                {hasFirstSubscriptionDiscount && (
                    <Chip
                        size="small"
                        color="success"
                        icon={<DiscountIcon fontSize="small" />}
                        label={`${couponInfo.descontoPercentual}% OFF aplicado somente na 1ª mensalidade`}
                        sx={{ mt: 1.5, fontWeight: 900 }}
                    />
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={closeCardDialog} disabled={aderindo} sx={{
                    color: CTA_GREEN_PLANOS,
                    borderColor: CTA_GREEN_PLANOS,
                    "&:hover": {
                        borderColor: CTA_GREEN_PLANOS_HOVER,
                        color: CTA_GREEN_PLANOS_HOVER,

                        // se quiser um "hover preenchido" levinho:
                        bgcolor: "rgba(0,0,0,0.04)", // ou use alpha(CTA_GREEN_PLANOS, 0.08)
                    },
                }}>
                    Voltar
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirmarCartao}
                    disabled={aderindo}
                    sx={{
                        fontWeight: 800, bgcolor: "#2ECC71", "&:hover": { bgcolor: "#27AE60" }
                    }}
                >
                    {aderindo ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Confirmar assinatura"}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // MODAL DE REUTILIZAÇÃO DO CARTÃO
    const ReuseDialog = (
        <Dialog
            open={reuseOpen}
            onClose={() => !aderindo && setReuseOpen(false)}
            fullWidth
            maxWidth="xs"
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    p: 0,
                    borderRadius: isMobile ? 0 : 3
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 900, pb: 1 }}>
                Confirmar pagamento
                <IconButton
                    onClick={() => !aderindo && setReuseOpen(false)}
                    sx={{ position: "absolute", right: 8, top: 10 }}
                    disabled={aderindo}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ pb: 3 }}>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
                    Detectamos um cartão salvo. Você pode prosseguir sem digitar os dados novamente.
                </Typography>

                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                        }}
                    >
                        <CreditCardRoundedIcon />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                            {String(reuseInfo?.brand || reuseInfo?.bandeira || "").toUpperCase()} •••• {reuseInfo?.last4}
                        </Typography>

                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Clique em “Usar outro cartão” para trocar.
                        </Typography>
                    </Box>
                </Paper>

                <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Plano selecionado:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 900 }}>
                        {String(reusePlan || "").toUpperCase()}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={() => {
                        if (aderindo) return;
                        setReuseOpen(false);
                        openCardDialog(reusePlan);
                    }}
                    disabled={aderindo}
                    sx={{ fontWeight: 900, textTransform: "none" }}
                >
                    Usar outro cartão
                </Button>

                <Button
                    variant="contained"
                    onClick={handleConfirmarReuse}
                    disabled={aderindo}
                    sx={{ fontWeight: 900, textTransform: "none" }}
                >
                    {aderindo ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Usar este cartão"}
                </Button>
            </DialogActions>
        </Dialog>
    );

    const PixDialog = (
        <PixSubscriptionDialog
            open={pixOpen}
            pixData={pixData}
            planName={pixData?.planName}
            amount={pixData?.amount}
            title="PIX mensal da assinatura"
            subtitle="Pague a mensalidade por PIX. O plano será liberado automaticamente assim que o pagamento for confirmado."
            closeLabel="Entendi"
            onClose={() => setPixOpen(false)}
            onPaid={handlePixPaid}
        />
    );


    // ========== DESKTOP ==========
    if (!isMobile) {
        return (
            <>
                <AppBar position="static" elevation={1} color="inherit" sx={{ borderRadius: "0 0 20px 20px" }}>
                    <Toolbar sx={{ justifyContent: "space-between" }}>
                        {/* Logo e título */}
                        <Box sx={{ display: 'flex', width: 44, overflow: 'hidden' }}>
                            <img
                                src="/docen-blue.png"
                                alt="Logo Docen"
                                style={{
                                    maxWidth: 100,
                                    width: 100,
                                    height: 'auto',
                                    display: 'block',
                                    transform: 'translateX(-28px)',
                                    flexShrink: 0
                                }}
                            />
                        </Box>
                        {/* Direita: Status, Botão, Select, Tema, Avatar */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                {StatusChip}
                                {PendingPixButton}
                                {PlanoButton}
                            </Box>
                            <Divider variant="middle" orientation="vertical" flexItem />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <ThemeToggle />
                                <AvatarMenu />
                            </Box>
                        </Box>
                    </Toolbar>
                </AppBar>
                {PlanModal}
                {ReuseDialog}
                {CardDialog}
                {PixDialog}
            </>
        );
    }

    // ========== MOBILE ==========
    return (
        <>
            <AppBar position="static" elevation={1} color="inherit" sx={{ borderRadius: "0 0 20px 20px" }}>
                <Toolbar sx={{ justifyContent: "space-between", minHeight: 48, px: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 56, height: 56, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
                            <Box
                                component="img"
                                src="/docen-blue.png"
                                alt="Logo Docen"
                                sx={{
                                    height: 58,
                                    width: 'auto',
                                    display: 'block',
                                    transform: 'translateX(-28px)',
                                    flexShrink: 0
                                }}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                        <IconButton sx={{ display: 'none' }} onClick={() => setDrawerOpen(true)} edge="end">
                            <MenuIcon sx={{ fontSize: 26 }} />
                        </IconButton>
                        <ThemeToggle small />
                        <AvatarMenu small />
                    </Box>
                </Toolbar>

                {/* Linha 2 (mobile): status + CTA sempre visíveis */}
                <Box sx={{ px: 1, pb: 1 }}>
                    <Box
                        sx={(theme) => ({
                            mx: "auto",
                            width: "fit-content",
                            maxWidth: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1,
                            py: 0.75,
                            borderRadius: 999,
                            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                            background: theme.palette.mode === "dark"
                                ? alpha(theme.palette.common.white, 0.04)
                                : alpha(theme.palette.common.black, 0.03)
                        })}
                    >
                        {StatusChip}
                        {PendingPixButton}

                        {PlanoButton && (
                            <Button
                                size="small"
                                onClick={openPlanModal}
                                disabled={aderindo}
                                sx={{
                                    px: 1,
                                    minWidth: 86,
                                    fontWeight: 900,
                                    borderRadius: 999,
                                    whiteSpace: "nowrap",
                                    background: "linear-gradient(90deg, #3b82f6 30%, #7f72f7 90%)",
                                    color: "#fff",
                                    boxShadow: "none",
                                    "&:hover": {
                                        background: "linear-gradient(90deg, #456ff2 30%, #6548da 90%)"
                                    }
                                }}
                            >
                                {aderindo ? <CircularProgress size={18} sx={{ color: "white" }} /> : statusConfig?.labelButton}
                            </Button>
                        )}
                    </Box>
                </Box>

            </AppBar>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        pt: 1,
                        pb: 2,
                        px: 0,
                        background: theme.palette.background.default,
                    }
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        mx: "auto",
                        position: "relative"
                    }}
                >
                    <IconButton
                        onClick={() => setDrawerOpen(false)}
                        sx={{
                            position: "absolute",
                            top: 5,
                            right: 0,
                            zIndex: 1
                        }}
                    >
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                    <Box sx={{ height: 60 }} />
                    <Stack
                        spacing={2}
                        alignItems="center"
                        sx={{ width: "100%", pb: 2 }}
                    >
                        <Divider variant="middle" orientation="horizontal" flexItem />
                        <Box sx={{ width: "100%" }}>{StatusChip}</Box>
                        <Box sx={{ width: "100%" }}>{PendingPixButton}</Box>
                        <Box sx={{ width: "100%" }}>{PlanoButton}</Box>
                    </Stack>
                </Box>
            </Drawer>
            {PlanModal}
            {ReuseDialog}
            {CardDialog}
            {PixDialog}

        </>
    );
}
